/**
 * Which reference is under a point, resolved on the CPU.
 *
 * A sibling of `findNetworkNodeAt` and `findNetworkVolumeAt`, and the third and last thing a
 * pointer can be over. The projection is theirs, for the reason they give: this has to agree with
 * what was drawn rather than approximate it.
 *
 * What differs is the shape being tested. A mark and a region are discs once projected, so a
 * distance to a centre answers; a reference is a polyline of up to `CURVE_SAMPLES` points, and the
 * nearest point on it is on one of its segments rather than at any vertex. So this measures to the
 * segments.
 */

import type { Size, ViewCamera } from '../../types';
import {
  applyViewProjection,
  clipToScreen,
  toViewProjectionMatrix,
} from '../../geometry/viewProjection';
import type { NetworkLayout } from '../types';

export type FindNetworkRouteArgs = {
  layout: NetworkLayout;
  camera: ViewCamera;
  /** In CSS pixels, matching the pointer coordinates. */
  viewport: Size;
  /** Cursor position within the canvas, in CSS pixels. */
  point: { x: number; y: number };
  /**
   * How far from a reference still counts as on it, in pixels.
   *
   * Larger than a mark's tolerance needs to be. A reference is drawn a little over a pixel wide
   * and has no interior to aim at, so hitting one exactly is a precision task rather than a
   * pointing one - and unlike a mark, overshooting costs nothing, since a reference has no
   * neighbours crowding it the way marks in a cluster do.
   */
  tolerance: number;
};

/**
 * The squared distance from a point to a segment, in screen space.
 *
 * Squared throughout, so the search does no square roots: the caller only ever compares these
 * against each other and against one threshold, and both comparisons hold under squaring.
 *
 * A degenerate segment - both ends projecting to the same pixel, which a bundled spline produces
 * wherever its samples bunch - falls out as the distance to that point, because the clamp holds
 * `t` at 0.
 */
const toSegmentDistanceSquared = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  const t =
    lengthSquared === 0
      ? 0
      : Math.max(
          0,
          Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSquared)
        );
  const offsetX = px - (ax + t * dx);
  const offsetY = py - (ay + t * dy);
  return offsetX * offsetX + offsetY * offsetY;
};

/**
 * The nearest reference under the point, as an index into `layout.routes`, or `null`.
 *
 * Nearest by screen distance rather than by depth, which is the opposite of how the marks
 * tie-break and is right for the same reason theirs is. Two overlapping marks are two discs and
 * the front one is the one drawn over the other, so depth decides; two references crossing are
 * two hairlines and neither hides the other, so the one the pointer is closer to is the one being
 * aimed at. Depth would hand back whichever happened to be in front, which at a crossing is not
 * something a reader can see.
 */
export const findNetworkRouteAt = ({
  layout,
  camera,
  viewport,
  point,
  tolerance,
}: FindNetworkRouteArgs): number | null => {
  if (viewport.width === 0 || viewport.height === 0) return null;

  const matrix = toViewProjectionMatrix(camera, viewport);
  const toleranceSquared = tolerance * tolerance;

  let closest: number | null = null;
  let nearest = toleranceSquared;

  layout.routes.forEach((route, routeIndex) => {
    /* Projected per segment rather than per route into an array: a route is two points on four of
       the five layouts, and allocating for the two of them that curve costs more than the repeat
       projection of a shared vertex. */
    for (let at = 0; at + 1 < route.length; at++) {
      const from = applyViewProjection(
        matrix,
        route[at][0],
        route[at][1],
        route[at][2]
      );
      const to = applyViewProjection(
        matrix,
        route[at + 1][0],
        route[at + 1][1],
        route[at + 1][2]
      );
      /* Either end behind the eye. Its projection wraps around the origin, so the segment drawn
         between them is not the segment measured here. Dropped rather than clipped: a reference
         with a vertex behind the camera is off the edge of a fitted view anyway. */
      if (from.w <= 0 || to.w <= 0) continue;

      const a = clipToScreen(from, viewport);
      const b = clipToScreen(to, viewport);
      const distanceSquared = toSegmentDistanceSquared(
        point.x,
        point.y,
        a.screenX,
        a.screenY,
        b.screenX,
        b.screenY
      );

      if (distanceSquared < nearest) {
        nearest = distanceSquared;
        closest = routeIndex;
      }
    }
  });

  return closest;
};
